/**
 * /picks/standings — screen 3r.
 *
 * A four-column mono grid: rank, name, points, points per pick.
 *
 *   D80 — both totals AND per-pick are always shown. Someone who joined in
 *         Week 4 has fewer points for an honest reason, and the per-pick column
 *         is the only thing that makes the comparison fair. Showing one without
 *         the other turns a late joiner into a bad picker.
 *   D82 — late joiners are annotated under their row rather than filtered into
 *         a separate board.
 *
 * The viewer's own row is tinted `surf` so it is findable in a long list
 * without scanning names.
 */

import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import type { StandingRow } from '@shared/types'
import { useStore } from '../lib/store'
import { ApiFailure, GET } from '../lib/api'
import { Body, Header, Notice, Screen, SkeletonCard, Spacer } from '../ui'

type Scope = 'season' | 'week'

interface StandingsResponse {
  season: number
  scope: Scope
  weekId: string | null
  standings: StandingRow[]
}

const GRID: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontVariantNumeric: 'tabular-nums',
  display: 'grid',
  gridTemplateColumns: '22px 1fr 46px 48px',
  gap: 8,
  alignItems: 'center',
}

/** "Week 4" → "Wk 4" — the annotation sits in a 11px line under a row. */
function shortWeek(label: string): string {
  return label.replace(/^Week\s+/i, 'Wk ')
}

/** ".787" — a leading zero would waste a character in a 48px column. */
function perPick(n: number): string {
  if (!Number.isFinite(n)) return '-'
  const s = n.toFixed(3)
  return s.startsWith('0') ? s.slice(1) : s
}

export default function Standings() {
  const { state } = useStore()
  const [scope, setScope] = useState<Scope>('season')
  const [rows, setRows] = useState<StandingRow[] | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async (next: Scope, signal?: AbortSignal) => {
    try {
      const season = state?.week?.seasonYear
      const qs = new URLSearchParams({ scope: next })
      if (season) qs.set('season', String(season))
      const res = await GET<StandingsResponse>(`/api/picks/standings?${qs.toString()}`, signal)
      setRows(res.standings)
      setErr(null)
    } catch (e) {
      if (signal?.aborted) return
      // Last-known-good stays on screen (D103).
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t load the standings.')
    }
  }, [state?.week?.seasonYear])

  useEffect(() => {
    const ac = new AbortController()
    void load(scope, ac.signal)
    return () => ac.abort()
  }, [load, scope])

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        title={<span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>Season standings</span>}
        sub={
          <div role="tablist" aria-label="Standings range" style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {([['season', 'Season'], ['week', 'This week']] as const).map(([value, label]) => {
              const on = scope === value
              return (
                <button
                  key={value}
                  role="tab"
                  aria-selected={on}
                  onClick={() => { if (!on) { setRows(null); setScope(value) } }}
                  className="mono"
                  style={{
                    fontSize: 11, fontWeight: 600, padding: '7px 14px', borderRadius: 999, minHeight: 44,
                    background: on ? 'var(--fill)' : 'transparent',
                    color: on ? 'var(--ink)' : 'var(--dim)',
                    border: on ? '1px solid var(--fill)' : '1px solid var(--line)',
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>
        }
      />

      <Body style={{ padding: '8px var(--gutter) calc(24px + var(--nav-h))' }}>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

        {!rows ? (
          <><SkeletonCard lines={4} /><Spacer h={12} /><SkeletonCard lines={4} /></>
        ) : rows.length === 0 ? (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Nobody has picked yet.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
              Someone has to go first.
            </div>
            <Link
              to="/picks"
              style={{
                display: 'inline-block', marginTop: 14, padding: '11px 20px', borderRadius: 999,
                background: 'var(--fill)', color: 'var(--ink)', fontSize: 13, fontWeight: 700,
              }}
            >
              Make your picks
            </Link>
          </div>
        ) : (
          <div role="table" aria-label={scope === 'season' ? 'Season standings' : 'This week’s standings'}>
            <div
              role="row"
              style={{
                ...GRID,
                fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase',
                color: 'var(--dim)', paddingBottom: 8,
              }}
            >
              <span role="columnheader" aria-label="Rank" />
              <span role="columnheader" aria-label="Name" />
              <span role="columnheader" style={{ textAlign: 'right' }}>Pts</span>
              <span role="columnheader" style={{ textAlign: 'right' }}>Per pick</span>
            </div>

            {rows.map((r, i) => (
              <div key={r.userId}>
                <Link
                  to={`/u/${encodeURIComponent(r.userId)}`}
                  role="row"
                  style={{
                    ...GRID,
                    fontSize: 13, padding: '12px 0', minHeight: 44,
                    borderTop: '1px solid var(--line)',
                    borderBottom: i === rows.length - 1 ? '1px solid var(--line)' : undefined,
                    background: r.isViewer ? 'var(--surf)' : undefined,
                    color: 'var(--fg)',
                  }}
                >
                  <span role="cell" style={{ color: r.isViewer ? 'var(--bright)' : 'var(--dim)' }}>{r.rank}</span>
                  <span role="cell" style={{ fontWeight: r.isViewer ? 600 : 400, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.displayName}{r.isViewer ? ' (you)' : ''}
                  </span>
                  <span role="cell" style={{ textAlign: 'right' }}>{r.points}</span>
                  <span role="cell" style={{ textAlign: 'right', color: 'var(--dim)' }}>{perPick(r.perPick)}</span>
                </Link>
                {r.joinedWeek ? (
                  <div style={{ fontSize: 11, color: 'var(--dim)', paddingLeft: 30 }}>
                    joined {shortWeek(r.joinedWeek)}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Body>
    </Screen>
  )
}
